import React, { useMemo } from "react";
import { useWowPath } from "../../../hooks/path-hooks";
import {
  EXPANSION,
  PATCH,
  getAbbreviatedClass,
  getAbbreviatedSpec,
  getFormattedNames,
  getIcyVeinsLinkName,
  getPrimaryStat,
} from "../../../lib/wowHelpers";
import NoData from "../../Common/NoData";
import { useJSONFetcher } from "@outplayed/json-fetcher";
import { API_WOW_ROOT_PATH } from "../../../api/apiHelpers";
import { useQueryString } from "../../../hooks/general-hooks";
import StatPriorities from "../Builds/StatPriorities";
import TalentTreeSection from "../Builds/TalentTreeSection";
import { validParams } from "../../../query_parameters/valid_parameters";
import DataDetails from "../../Common/DataDetails";
import PvPStatSection from "./PvPStatSection";
import PvPTalents from "./PvPTalents";
import TierSetContainer from "../Builds/TierSetContainer";
import { DateTime } from "luxon";
import Layout from "../Layout";
import PvPGearGuide from "../../GearGuide/PvPGearGuide";
import PageSectionCrossLinks from "../../CrossLinks/PageSectionCrossLinks";
import { WoWContext } from "../../App";

const PvPTalentsContainer = () => {
  const { classType, specType } = useWowPath();
  const { gameMode } = useQueryString();
  const { displayClassName, displaySpecName, dataClassName, dataSpecName } = getFormattedNames(classType, specType);

  const { data: pvpData, loading: isLoading } = useJSONFetcher(
    `${API_WOW_ROOT_PATH}/PVP/${gameMode ?? "3v3"}/${dataClassName}.json`,
    {
      ssr: true,
    },
  );

  const specs = validParams.builds.specs[classType];
  const allTrees = {};
  for (let i = 0; i < 4; i++) {
    const { data: specData } = useJSONFetcher(`https://static.bigbrain.gg/wow/tree/v9/${classType}_${specs[i]?.value}.json`, {
      ssr: true,
    });
    allTrees[specs[i]?.value] = specData;
  }

  const classAndSpecTree = allTrees[specType];

  const statPriorities = useMemo(() => {
    const primaryStat = getPrimaryStat(dataClassName, dataSpecName);
    const secondaryStats = pvpData?.[displaySpecName]?.stat_priorities?.stat_priorities?.split("-") ?? [];
    if (primaryStat) {
      return [primaryStat, ...secondaryStats];
    }
    return secondaryStats;
  }, [pvpData, specType]);

  if (isLoading) return null;

  const talentTreeSectionData = {
    classType,
    mainStat: pvpData?.[displaySpecName]?.talents?.max_rating ?? 0,
    popularity: parseFloat(pvpData?.[displaySpecName]?.talents?.popularity ?? 0).toFixed(1) + "%",
    selectedTalents: pvpData?.[displaySpecName]?.talents?.talent?.split("-") ?? [],
    classAndSpecTree,
    specType,
    statLabel: "Max Rating",
    statTooltip: "The highest rated player that used this particular talent build in the selected competitive PvP game mode",
    allTrees,
    encounterType: "PvP",
  };

  const pvpContextValue = {
    allTrees,
    selectedTalents: pvpData?.[displaySpecName]?.talents?.talent?.split("-") ?? [],
    specType,
  };

  const hasData = (pvpData?.[displaySpecName]?.stats?.played ?? 0) > 0;

  const pageDescription = `Everything for ${displaySpecName} ${displayClassName} PVP. The best ${getAbbreviatedSpec(
    displaySpecName,
  )} ${getAbbreviatedClass(
    displayClassName,
  )} talents, from top gear to stat priorities, data-driven by top players. ${EXPANSION} ${PATCH}.`;

  return (
    <Layout
      className="wow-pvp-container"
      classType={classType}
      filterPage="pvp"
      metaTitle={`${displaySpecName} ${displayClassName} PvP - ${EXPANSION} ${PATCH}`}
      pageDescription={pageDescription}
      pageTitle="PvP Talents, Stats, and Gear"
      specType={specType}
    >
      {hasData ? (
        <>
          <WoWContext.Provider value={pvpContextValue}>
            <DataDetails
              encounterType="PvP"
              numLogsTitle="Top Rated Players Analyzed"
              timestamp={DateTime.fromISO(pvpData?.[displaySpecName]?.stats?.timestamp ?? "").toRelative()}
              parses={pvpData?.[displaySpecName]?.stats?.unique_player_count ?? 0}
              ratingRange={`${pvpData?.[displaySpecName]?.stats?.min_rating ?? 0}-${
                pvpData?.[displaySpecName]?.stats?.max_rating ?? 0
              }`}
              useDetailedTitle
            />
            <PvPStatSection pvpData={pvpData?.[displaySpecName]} />
            <StatPriorities statPriorities={statPriorities} encounterType="PvP" />
            <TalentTreeSection {...talentTreeSectionData} />
            <PvPTalents
              displayClassName={displayClassName}
              displaySpecName={displaySpecName}
              pvpTalents={pvpData?.[displaySpecName]?.pvp_talents ?? []}
            />
            <PvPGearGuide
              builds={pvpData?.[displaySpecName]?.items}
              combos={pvpData?.[displaySpecName]?.combos}
              classType={classType}
              specType={specType}
              encounterType="PvP"
            />
            <TierSetContainer className={displayClassName} specName={displaySpecName} encounterType="PvP" />
            <PageSectionCrossLinks
              classType={classType}
              description={`Our friends at Icy Veins have you covered, with an in-depth guide to ready your ${displaySpecName} ${displayClassName} for PvP.`}
              guideData={[
                {
                  description: `The best in-depth guides covering PvP for ${displaySpecName} ${displayClassName}`,
                  link: `https://www.icy-veins.com/wow/${getIcyVeinsLinkName(specType)}-${getIcyVeinsLinkName(
                    classType,
                  )}-pvp-guide`,
                  title: "PvP Guide",
                },
              ]}
              specType={specType}
            />
          </WoWContext.Provider>
        </>
      ) : (
        <NoData specType={specType} />
      )}
    </Layout>
  );
};

export default PvPTalentsContainer;
